import React from "react";
import EtCreationAreaIcon1SVGComp from "@/assets/img/icons/svgComp/EtCreationAreaIcon1SVGComp";
import EtCreationTypeIcon1SVGComp from "@/assets/img/icons/svgComp/EtCreationTypeIcon1SVGComp";
import { createSvgComponentFromBase64 } from "@/utils/createSvgComponentFromBase64";

const EtCreationSummaryTiles = ({
  creations = [],
  creationTypes = [],
  creationAreaTypes = [],
}) => {

  // First Created - 09 Dec 2025
  // Author - Atul Pandey
  // Purpose - Tiled summary view of creations by Creation Areas & Creation Types
  // Inspired by EtImprintCreation Summary tab, adapted for Trinity Creation Center


  // -----------------------------------------
  // 1. Derived Scorecard Data
  // -----------------------------------------
  const creationTypesData = React.useMemo(
    () => extractCreationTypesData(creationTypes, creations),
    [creationTypes, creations]
  );
  const creationAreaTypesData = React.useMemo(
    () => extractCreationAreaTypesData(creationAreaTypes, creations),
    [creationAreaTypes, creations]
  );

  return (
    <div className="mt-6 bg-gray-50 rounded p-4">
      {/* Intro */}
      {creations?.length > 0 && (
        <p className="text-xs md:text-sm m-1 p-1 leading-5 text-gray-700">
          Here&apos;s a bird&apos;s-eye view of your{" "}
          <span className="font-semibold text-orange-700">creations imprint</span>, 
          capturing <span className="font-semibold">where</span> you&apos;ve been
          creating value and <span className="font-semibold">what</span> you&apos;ve been
          shaping. On the left, your creations are organized by{" "}
          <strong>Creation Areas</strong> like Industries, Domains, Functions,
          Tech, Sales, and Services — reflecting the breadth of your focus. On
          the right, they are grouped by <strong>Creation Types</strong> such as
          templates, checklists, solutions, and products — the forms your work
          takes.
        </p>
      )}

      {/* Two Column Scorecards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 w-full md:w-5/6 mx-auto">
        {/* Creation Areas */}
        <div className="bg-white rounded p-3 shadow-sm">
          <div className="flex items-center mb-1">
            <EtCreationAreaIcon1SVGComp className="h-5 w-5 text-etpx-5" />
            <p className="p-1 m-1 text-gray-700 font-bold text-sm">
              Creation Areas
            </p>
          </div>
          <p className="mx-1 px-1 text-[10px] md:text-xs italic text-gray-500 mb-2">
            who have I created for..
          </p>
          <CreationAreaTypesScorecard data={creationAreaTypesData} />
        </div>

        {/* Creation Types */}
        <div className="bg-white rounded p-3 shadow-sm">
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center">
              <EtCreationTypeIcon1SVGComp className="h-5 w-5 text-etpx-5" />
              <p className="p-1 m-1 text-gray-700 font-bold text-sm">
                Creation Types
              </p>
            </div>
          </div>
          <p className="mx-1 px-1 text-[10px] md:text-xs italic text-gray-500 mb-2">
            what have I created...
          </p>
          <CreationTypesScorecard data={creationTypesData} />
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------
// Helper: Creation Types Scorecard
// -----------------------------------------
const CreationTypesScorecard = ({ data = [] }) => {
  const gridSize = calculateGridSize(data.length);

  return (
    <div
      className={`grid grid-cols-${gridSize} aspect-square rounded bg-gradient-to-r from-black to-black via-etpx-bg mx-auto p-[2px]`}
    >
      {data.map((item, index) => {
        const MyIcon = createSvgComponentFromBase64(item.pic);
        const colorClass = item.creationCount > 0 ? "bg-etpx-5" : "bg-black";
        const textColorClass =
          item.creationCount > 0 ? "text-white" : "text-gray-900";
        const textTitleColorClass =
          item.creationCount > 0 ? "text-black" : "text-gray-700";

        return (
          <div
            key={index}
            className={`aspect-square rounded border m-[1px] border-black ${colorClass}`}
          >
            <div className="flex flex-col justify-between items-center h-full p-2">
              <MyIcon
                className={`h-4 w-4 sm:h-6 sm:w-6 md:h-8 md:w-8 ${textColorClass}`}
              />
              <div
                className={`rounded-full h-6 w-6 border flex justify-center items-center ${
                  item.creationCount > 0 ? "border-white" : "border-none"
                }`}
              >
                <p className={`${textColorClass} text-center font-bold`}>
                  {item.creationCount}
                </p>
              </div>
              <p
                className={`text-[9px] ${textTitleColorClass} text-center leading-tight`}
              >
                {item.creationTypeName}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// -----------------------------------------
// Helper: Creation Area Types Scorecard
// -----------------------------------------
const CreationAreaTypesScorecard = ({ data = [] }) => {
  const gridSize = calculateGridSize(data.length);

  return (
    <div
      className={`grid grid-cols-${gridSize} aspect-square rounded bg-gradient-to-r from-black to-black via-etpx-bg mx-auto p-[2px]`}
    >
      {data.map((item, index) => {
        const MyIcon = createSvgComponentFromBase64(item.pic);
        const colorClass = item.creationCount > 0 ? "bg-etpx-5" : "bg-black";
        const textColorClass =
          item.creationCount > 0 ? "text-white" : "text-gray-900";
        const textTitleColorClass =
          item.creationCount > 0 ? "text-black" : "text-gray-700";

        return (
          <div
            key={index}
            className={`aspect-square rounded border m-[1px] border-black ${colorClass}`}
          >
            <div className="flex flex-col justify-between items-center h-full p-2">
              <MyIcon className={`h-12 w-12 ${textColorClass}`} />
              <div
                className={`rounded-full h-8 w-8 border flex justify-center items-center ${
                  item.creationCount > 0 ? "border-white" : "border-none"
                }`}
              >
                <p className={`${textColorClass} text-center font-bold text-2xl`}>
                  {item.creationCount}
                </p>
              </div>
              <p className={`text-xs ${textTitleColorClass} text-center`}>
                {item.creationAreaTypeName}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// -----------------------------------------
// Helper: Grid size + Data extractors
// -----------------------------------------
const calculateGridSize = (n) => {
  if (n <= 1) return 1;
  if (n <= 4) return 2;
  if (n <= 9) return 3;
  if (n <= 16) return 4;
  return 4;
};

const extractCreationTypesData = (creationTypes = [], creations = []) => {
  const creationCountMap = {};

  creations?.forEach((creation) => {
    const creationTypeId = creation.userCreationType?._id;
    if (creationTypeId) {
      creationCountMap[creationTypeId] =
        (creationCountMap[creationTypeId] || 0) + 1;
    }
  });

  return creationTypes?.map((type) => ({
    creationTypeName: type.creationTypeName,
    pic: type.pic,
    creationCount: creationCountMap[type._id] || 0,
  }));
};

const extractCreationAreaTypesData = (
  creationAreaTypes = [],
  creations = []
) => {
  const creationCountMap = {};

  creations?.forEach((creation) => {
    creation.userCreationFocusAreas?.forEach((focusArea) => {
      const creationAreaTypeId =
        focusArea?.creationAreaType?._id || focusArea?.creationAreaType;
      if (creationAreaTypeId) {
        creationCountMap[creationAreaTypeId] =
          (creationCountMap[creationAreaTypeId] || 0) + 1;
      }
    });
  });

  return creationAreaTypes?.map((type) => ({
    creationAreaTypeName: type.creationAreaTypeName,
    pic: type.pic,
    creationCount: creationCountMap[type._id] || 0,
  }));
};

export default EtCreationSummaryTiles;